<template>
    <div>
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h3 class="mb-0">{{ trans('maintainers.overview.title') }}</h3>
            <div class="d-flex align-items-end center gap-3">
                <small class="text-muted">{{
                    trans('maintainers.overview.results.count', {
                        total: maintainers?.data.length,
                    })
                }}</small>

                <modal-link v-if="!isTablet && hasAnyPermission('maintainer.create')" :href="route('maintainers.create')">{{
                    trans('maintainers.overview.action.add')
                }}</modal-link>
            </div>
        </div>

        <deferred data="maintainers">
            <template #fallback>
                <spinner />
            </template>
            <div v-if="maintainers.data.length === 0">
                <card style="padding: 0 !important">
                    <div class="text-gray text-center">
                        <div class="text-semi-bold">
                            {{ trans('stakeholders.overview.empty.maintainers.title') }}
                        </div>
                        <div>
                            {{ trans('stakeholders.overview.empty.description') }}
                        </div>
                    </div>
                </card>
            </div>
            <div v-else>
                <card
                    v-for="maintainer in maintainers.data"
                    :key="maintainer.id"
                    card-classes="mb-2"
                    style="padding: 0 !important"
                    @click.stop="toggleMaintainer(maintainer)"
                >
                    <header
                        :style="{
                            top: isDesktop ? '128px' : '55px',
                            margin: isDesktop ? '-16px' : '-11px',
                            padding: isDesktop ? '16px 16px 8px' : '10px 10px 5px',
                        }"
                        class="d-flex justify-content-between align-items-center position-sticky mb-3 bg-white"
                        style="z-index: 1; border-top-right-radius: var(--c-card-border-radius); border-top-left-radius: var(--c-card-border-radius)"
                    >
                        <h4 class="h6 d-flex align-items-center gap-2">
                            <arrow-down
                                v-if="isTablet && maintainer.serviceLevelAgreements.length"
                                :class="{
                                    collapsed: isMaintainerCollapsed(maintainer),
                                }"
                                class="collapse-toggle d-md-none text-primary"
                            />
                            {{ maintainer.name }}
                        </h4>
                        <div v-if="!isTablet && hasAnyPermission(['maintainer.update', 'maintainer.delete'])" class="d-flex gap-1">
                            <modal-link v-if="hasAnyPermission('maintainer.update')" :href="route('maintainers.slas.create', maintainer)"
                                >{{ trans('maintainers.overview.action.add_sla') }}
                            </modal-link>
                            <modal-link
                                v-if="hasAnyPermission('maintainer.update')"
                                :href="route('maintainers.edit', maintainer)"
                                :tooltip="trans('general.action.edit')"
                                icon="cil-pencil"
                            />
                            <jet-button
                                v-if="hasAnyPermission('maintainer.delete')"
                                v-c-tooltip="{
                                    content: trans('general.action.delete'),
                                }"
                                :destructive="true"
                                class="btn-icon"
                                type="button"
                                variant="secondary"
                                @click="deleteMaintainer(maintainer)"
                            >
                                <icon name="cil-trash" />
                            </jet-button>
                        </div>
                    </header>
                    <maintainer-general-info :maintainer="maintainer" />
                    <service-level-agreements-list :collapsed="isMaintainerCollapsed(maintainer)" :maintainer="maintainer" />
                </card>
            </div>
        </deferred>
    </div>
</template>
<script setup>
import JetButton from '@/Jetstream/Button.vue';
import { eventBus, events } from '@/eventBus.js';
import { isDesktop, isTablet } from '@/Utils/UseMedia';
import { Deferred, usePage } from '@inertiajs/vue3';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import ModalLink from '@/Components/ModalLink.vue';
import { computed, ref } from 'vue';
import ServiceLevelAgreementsList from './ServiceLevelAgreementsList.vue';
import MaintainerGeneralInfo from './MaintainerGeneralInfo.vue';
import ArrowDown from '@/../icons/ArrowDown.vue';
import Card from '@/Components/Card.vue';

const page = usePage();
const expandedMaintainer = ref(null);
const maintainers = computed(() => page.props.maintainers);
const deleteMaintainer = (item) => {
    eventBus.$emit(events.openConfirmDeleteStakeholderModal, {
        stakeholder: item,
        type: 'maintainer',
    });
};
const toggleMaintainer = (maintainer) => {
    if (!isTablet.value) return;

    if (expandedMaintainer.value === maintainer.id) {
        expandedMaintainer.value = null;
    } else {
        expandedMaintainer.value = maintainer.id;
    }
};

const isMaintainerCollapsed = (maintainer) => {
    if (!isTablet.value) return false;

    return expandedMaintainer.value !== maintainer.id;
};
</script>
